import DiagnosisStatusBadge from 'components/badge/DiagnosisStatusBadge';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { DivConst } from 'constants/divConst';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import WsReportAttributeDistribution from 'pages/ws/components/reportAttributeDistribution/WsReportAttributeDistribution';
import WsReportBehaviorAnalytics from 'pages/ws/components/reportBehaviorAnalytics/WsReportBehaviorAnalytics';
import WsReportChart from 'pages/ws/components/reportChart/WsReportChart';
import WsReportResultDistribution from 'pages/ws/components/reportResultDistribution/WsReportResultDistribution';
import WsReportAnswerCombinationRanking from 'pages/ws/components/reportAnswerCombinationRanking/WsReportAnswerCombinationRanking';
import WsReportQuestionAnswer from 'pages/ws/components/reportQuestionAnswer/WsReportQuestionAnswer';
import WsModalReportDownload from 'pages/ws/components/modalReportDownload/WsModalReportDownload';
import WsModalReportTerm from 'pages/ws/components/modalReportTerm/WsModalReportTerm';
import WsReportDetailHeader from 'pages/ws/components/reportDetailHeader/WsReportDetailHeader';
import WsReportTermSection from 'pages/ws/components/reportTermSection/WsReportTermSection';
import WsReportMetrics from 'pages/ws/components/reportMetrics/WsReportMetrics';
import { useWsReportDiagnosis } from './hooks/useWsReportDiagnosis';

import type { JSX } from 'react';

/**
 * 診断レポート詳細画面
 */
export default function WsReportDiagnosis(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const navigate = useNavigate();
  const { diagnosisCd } = useParams();

  const [acctState] = useAtom(AcctState);

  const {
    initialized,
    processing,
    diagnosis,
    reportDateFrom,
    reportDateTo,
    isExpiredPlan,
    modalTermOpen,
    setModalTermOpen,
    modalDownloadOpen,
    setModalDownloadOpen,
    onContentOpen,
    onModalReportDownloadOpen,
    onModalReportTermOpen,
    onChangeReportTerm,
  } = useWsReportDiagnosis(diagnosisCd, acctState.company);

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* 初期化中の表示 */}
      <IndicatorScreen visible={!initialized || processing} />
      {/* 初期化後の表示 */}
      {initialized && diagnosis && (
        <>
          <WsReportDetailHeader
            imageKey={diagnosis.imageKey}
            title={diagnosis.title}
            createdAt={diagnosis.createdAt}
            statusBadge={<DiagnosisStatusBadge diagnosisStatusDiv={diagnosis.diagnosisStatusDiv} />}
            isExpiredPlan={isExpiredPlan}
            onBack={() => navigate(-1)}
            onContentOpen={onContentOpen}
            onModalReportDownloadOpen={onModalReportDownloadOpen}
          />

          <WsReportTermSection
            reportDateFrom={reportDateFrom}
            reportDateTo={reportDateTo}
            onModalReportTermOpen={onModalReportTermOpen}
          />

          <WsReportMetrics
            participatedCount={diagnosis.participatedCount}
            lineFlag={diagnosis.lineFlag}
            mailAddressCount={diagnosis.mailAddressCount}
            lineCount={diagnosis.lineCount}
            mailAddressAcquisitionRate={diagnosis.mailAddressAcquisitionRate}
            lineAddressAcquisitionRate={diagnosis.lineAddressAcquisitionRate}
            answeredCount={diagnosis.answeredCount}
            answeredRate={diagnosis.answeredRate}
          />

          <WsReportChart reports={diagnosis.reports} lineFlag={diagnosis.lineFlag} />

          <WsReportBehaviorAnalytics behaviorAnalytics={diagnosis.behaviorAnalytics} />

          {/* 診断結果の分布・属性分布・質問別回答は参加者がいる場合のみ表示 */}
          {diagnosis.participatedCount > 0 && (
            <>
              <section className="section-reports-top">
                <div className="container">
                  <div className="row">
                    <div className="col-lg-6 section-reports-left-col">
                      {diagnosis.resultDistribution && diagnosis.resultDistribution.length > 0 && (
                        <WsReportResultDistribution
                          resultDistribution={diagnosis.resultDistribution}
                        />
                      )}
                    </div>
                    <div className="col-lg-6 section-reports-right-col">
                      {diagnosis.attributeDistribution &&
                        (diagnosis.attributeDistribution.gender.length > 0 ||
                          diagnosis.attributeDistribution.ageGroup.length > 0 ||
                          diagnosis.attributeDistribution.prefecture.length > 0) && (
                          <WsReportAttributeDistribution
                            attributeDistribution={diagnosis.attributeDistribution}
                          />
                        )}
                    </div>
                  </div>
                  {/* 質問別回答選択パターン */}
                  {Array.isArray(diagnosis.questionAnswerDistribution) &&
                    diagnosis.questionAnswerDistribution.length > 0 && (
                      <div className="row row--question-answer">
                        <div className="col-12">
                          <WsReportQuestionAnswer
                            questionAnswerDistribution={diagnosis.questionAnswerDistribution}
                          />
                        </div>
                      </div>
                    )}
                </div>
              </section>

              {/* 回答パターンランキング（質問別回答選択パターンと別カード） */}
              {Array.isArray(diagnosis.answerCombinationRanking) &&
                diagnosis.answerCombinationRanking.length > 0 && (
                  <section className="section-reports-top section-reports-top--answer-ranking">
                    <div className="container">
                      <div className="row">
                        <div className="col-12">
                          <WsReportAnswerCombinationRanking
                            answerCombinationRanking={diagnosis.answerCombinationRanking}
                            questionAnswerDistribution={diagnosis.questionAnswerDistribution}
                          />
                        </div>
                      </div>
                    </div>
                  </section>
                )}
            </>
          )}
        </>
      )}

      {/* レポート期間設定モーダル */}
      <WsModalReportTerm
        open={modalTermOpen}
        setOpen={setModalTermOpen}
        reportDateFrom={reportDateFrom}
        reportDateTo={reportDateTo}
        onSubmit={attr => onChangeReportTerm(attr.reportDateFrom, attr.reportDateTo)}
      />
      {/* レポートダウンロードモーダル */}
      <WsModalReportDownload
        open={modalDownloadOpen}
        setOpen={setModalDownloadOpen}
        cd={diagnosisCd}
        contentsType={DivConst.items.contentsType.diagnosis.value}
        reportDateFrom={null}
        reportDateTo={null}
      />
      {
        // プラン（利用期間切れ）の場合に表示する
        isExpiredPlan && <Tooltip id="custom-tooltip" place="top" variant="info" />
      }
    </>
  );
}
